export const TREE_ZOOM_STEPS = 4;
export const TREE_NODE_GAP = 28;
export const TREE_MAX_SCALE = 1.1;
export const TREE_ZOOM_PROGRESS = [0, 1 / 3, 2 / 3, 1];

export function categoryTreeData(categories) {
  function visit(category, depth, ancestors, prefix) {
    if (!category?.id || !category?.slug || depth > 5 || ancestors.has(String(category.id))) return null;
    const key = `${prefix}/${category.id}`;
    const next = new Set(ancestors).add(String(category.id));
    return {
      ...category, title: String(category.title || category.name || 'دسته‌بندی'), key, depth,
      children: (Array.isArray(category.children) ? category.children : [])
        .map((child) => visit(child, depth + 1, next, key)).filter(Boolean),
    };
  }
  return { key: 'root', title: 'پارس پایپینگ', depth: 0, children: (categories || []).map((c) => visit(c, 1, new Set(), 'root')).filter(Boolean) };
}

export function categoryTreePath(category) {
  if (!category?.slug) return '/';
  // Category leaves remain tables; only the existing table can expose enabled product-detail links.
  return `/${Number(category.level || category.depth) === 1 ? 'category' : 'subcategory'}/${encodeURIComponent(category.slug)}`;
}

export function overviewCamera(nodes, width, height) {
  const extentX = Math.max(1, ...nodes.map((node) => Math.abs(node.px) + (node.radius || 62)));
  const extentY = Math.max(1, ...nodes.map((node) => Math.abs(node.py) + (node.radius || 62)));
  const k = Math.max(0.0001, Math.min(0.72, Math.max(1, width - 40) / (extentX * 2), Math.max(1, height - 40) / (extentY * 2)));
  return { x: width / 2, y: height / 2, k };
}

export function treeZoomScale(overview, width, height, step, maxRadius = 120) {
  const max = Math.max(overview.k, Math.min(TREE_MAX_SCALE, (Math.min(width, height) - 40) / (maxRadius * 2)));
  return overview.k * Math.pow(max / overview.k, TREE_ZOOM_PROGRESS[step]);
}

export function nearestTreeNode(nodes, point, camera) {
  let closest = null;
  let distance = Infinity;
  for (const node of nodes) {
    if (!node.depth) continue;
    const value = Math.hypot(node.px * camera.k + camera.x - point[0], node.py * camera.k + camera.y - point[1]);
    if (value < distance) { distance = value; closest = node; }
  }
  return closest;
}

export function focusCamera(node, width, height, overview, step, maxRadius = 120) {
  const family = [node, ...(node.children || [])];
  const left = Math.min(...family.map((item) => item.px - (item.radius || 62)));
  const right = Math.max(...family.map((item) => item.px + (item.radius || 62)));
  const top = Math.min(...family.map((item) => item.py - (item.radius || 62)));
  const bottom = Math.max(...family.map((item) => item.py + (item.radius || 62)));
  const fit = Math.min(Math.max(1, width - 48) / Math.max(right - left, 1), Math.max(1, height - 48) / Math.max(bottom - top, 1));
  const limit = treeZoomScale(overview, width, height, step, maxRadius);
  const radius = node.radius || 62;
  const circleLimit = Math.max(overview.k, (Math.min(width, height) - 40) / (radius * 2));
  const k = Math.min(limit, circleLimit);
  const maxX = Math.max(0, (width / 2 - 20) / k - radius);
  const maxY = Math.max(0, (height / 2 - 20) / k - radius);
  const familyWeight = (1 - TREE_ZOOM_PROGRESS[step]) * Math.min(1, fit / k);
  const cx = Math.max(node.px - maxX, Math.min(node.px + maxX, node.px + ((left + right) / 2 - node.px) * familyWeight));
  const cy = Math.max(node.py - maxY, Math.min(node.py + maxY, node.py + ((top + bottom) / 2 - node.py) * familyWeight));
  return { k, x: width / 2 - cx * k, y: height / 2 - cy * k };
}

export function categoryTreeRadius(title) {
  const measure = (text) => Array.from(text).reduce((sum, letter) => sum + (/[MW@#%]/.test(letter) ? 14 : /\s/.test(letter) ? 4 : 9), 0);
  const words = String(title || '').trim().split(/\s+/).filter(Boolean).map(measure);
  let radius = Math.max(62, Math.ceil((Math.sqrt(measure(String(title || '')) * 26) + 24) / Math.SQRT2));
  // Reserve whole-word lines inside the circle's inscribed square, not just total text area.
  for (let attempt = 0; attempt < 256; attempt++, radius += 3) {
    const inner = radius * Math.SQRT2 - 18;
    let lines = 1, used = 0;
    for (const width of words) {
      if (used && used + 4 + width <= inner) { used += 4 + width; continue; }
      if (used) lines++;
      const parts = Math.max(1, Math.ceil(width / inner));
      lines += parts - 1;
      used = width - (parts - 1) * inner;
    }
    if (lines * 21 + 4 <= inner) break;
  }
  return radius;
}

export function categoryTreeRadii(nodes) {
  // Every depth shares a size; even a long leaf title must not outgrow its ancestors.
  const smallest = Math.max(62, ...nodes.filter((node) => node.depth).map((node) => categoryTreeRadius(node.data?.title || node.title) - (5 - node.depth) * 8));
  const radii = Array.from({ length: 6 }, (_, depth) => smallest + (5 - depth) * 8);
  radii[0] = radii[1] + 50;
  return radii;
}

export function layoutTreeGeometry(branches, rootRadius = 124, aspectRatio = 1) {
  const root = { key: 'root', px: 0, py: 0, radius: rootRadius, depth: 0 };
  const byKey = new Map([[root.key, root]]);
  const nonempty = branches.filter((branch) => branch.length);
  const weights = nonempty.map((branch) => {
    const parents = new Set(branch.map((item) => item.parentKey));
    return Math.pow(branch.filter((item) => !parents.has(item.key)).length, 0.65);
  });
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const levels = Array.from({ length: 6 }, () => []);
  let start = 0;
  nonempty.forEach((branch, index) => {
    const span = weights[index] / total * Math.PI * 2;
    const center = start + span / 2;
    const spread = Math.max(1, ...branch.map((item) => Math.abs(item.tangent)));
    branch.forEach((item) => {
      const angle = center + item.tangent / spread * span * 0.44;
      levels[item.depth].push({ ...item, angle });
    });
    start += span;
  });
  const placed = [root];
  const aspect = Math.max(0.6, Math.min(2.5, aspectRatio || 1));
  levels.slice(1).forEach((nodes) => {
    nodes.forEach((node) => {
      const parent = byKey.get(node.parentKey);
      const dx = Math.sin(node.angle) * aspect, dy = -Math.cos(node.angle);
      const length = Math.hypot(dx, dy);
      const ux = dx / length, uy = dy / length;
      let radial = ((parent.baseRadial || 0) + parent.radius + node.radius + TREE_NODE_GAP + 24) * length;
      // Stagger crowded siblings outward instead of moving an entire depth to a distant ring.
      for (let pass = 0; pass <= placed.length; pass++) {
        let moved = false;
        for (const other of placed) {
          const along = other.px * ux + other.py * uy;
          const across = other.px * uy - other.py * ux;
          const distance = node.radius + other.radius + TREE_NODE_GAP;
          if (Math.abs(across) >= distance) continue;
          const reach = Math.sqrt(distance * distance - across * across);
          if (radial > along - reach && radial < along + reach) {
            radial = along + reach + 0.001;
            moved = true;
          }
        }
        if (!moved) break;
      }
      const position = { ...node, radial, baseRadial: radial / length, px: ux * radial, py: uy * radial };
      placed.push(position);
      byKey.set(node.key, position);
    });
  });
  return byKey;
}
